import React, { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent } from "./ui/dialog";
import { X, Plus, Camera, Trash2 } from "lucide-react";
import api, { fileUrl } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import SmartImage from "./SmartImage";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";

function StoryViewer({ groups, startIndex, onClose, currentUserId, onDeleted }) {
  const [groupIdx, setGroupIdx] = useState(startIndex);
  const [storyIdx, setStoryIdx] = useState(0);
  const [deleting, setDeleting] = useState(false);
  const [paused, setPaused] = useState(false);  // pause auto-advance during confirm
  const group = groups[groupIdx];
  const story = group?.stories?.[storyIdx];
  const TICK_MS = 80;
  const TOTAL_MS = story?.media_type === "video" ? 12000 : 5000;
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    setProgress(0);
    if (!story || paused) return;
    const start = Date.now();
    const i = setInterval(() => {
      const pct = Math.min(100, ((Date.now() - start) / TOTAL_MS) * 100);
      setProgress(pct);
      if (pct >= 100) {
        clearInterval(i);
        next();
      }
    }, TICK_MS);
    return () => clearInterval(i);
    // eslint-disable-next-line
  }, [groupIdx, storyIdx, paused]);

  const next = () => {
    if (!group) return;
    if (storyIdx + 1 < group.stories.length) {
      setStoryIdx((s) => s + 1);
    } else if (groupIdx + 1 < groups.length) {
      setGroupIdx((g) => g + 1);
      setStoryIdx(0);
    } else {
      onClose();
    }
  };
  const prev = () => {
    if (storyIdx > 0) setStoryIdx((s) => s - 1);
    else if (groupIdx > 0) { setGroupIdx((g) => g - 1); setStoryIdx(0); }
  };

  const handleDelete = async (e) => {
    e?.stopPropagation();
    if (!story || deleting) return;
    setPaused(true);
    const ok = window.confirm("Delete this story? This can't be undone.");
    if (!ok) { setPaused(false); return; }
    setDeleting(true);
    try {
      await api.delete(`/stories/${story.story_id}`);
      toast.success("Story deleted");
      onDeleted?.(story.story_id);
      // Reset progress and advance if there are more stories in this group;
      // otherwise close the viewer entirely.
      const remaining = (group?.stories?.length || 0) - 1;
      if (remaining <= 0) {
        onClose();
      } else {
        // Drop the deleted story locally so the viewer advances cleanly
        if (storyIdx >= remaining) setStoryIdx(Math.max(0, remaining - 1));
        setPaused(false);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't delete story");
      setPaused(false);
    } finally {
      setDeleting(false);
    }
  };

  const isOwner = !!currentUserId && !!group && group.vendor_id === currentUserId;

  if (!story) return null;
  return (
    <div className="fixed inset-0 z-50 bg-black" data-testid="story-viewer">
      {/* Progress bars */}
      <div className="absolute top-3 left-3 right-3 z-10 flex gap-1.5">
        {group.stories.map((_, i) => (
          <div key={i} className="flex-1 h-0.5 rounded bg-white/30 overflow-hidden">
            <div
              className="h-full bg-white transition-all"
              style={{
                width: i < storyIdx ? "100%" : i === storyIdx ? `${progress}%` : "0%",
              }}
            />
          </div>
        ))}
      </div>

      {/* Header */}
      <div className="absolute top-6 left-3 right-3 z-10 flex items-center gap-2.5">
        <div className="w-9 h-9 rounded-full overflow-hidden bg-pink-500/30 grid place-items-center border-2 border-white">
          {group.vendor_avatar ? (
            <img src={fileUrl(group.vendor_avatar)} alt="" className="w-full h-full object-cover" />
          ) : (
            <span className="text-white font-black">{group.vendor_name[0]}</span>
          )}
        </div>
        <span className="text-white text-sm font-bold drop-shadow">@{group.vendor_name}</span>
        {isOwner && (
          <button
            onClick={handleDelete}
            disabled={deleting}
            className="ml-auto w-9 h-9 grid place-items-center rounded-full bg-black/40 backdrop-blur hover:bg-red-500/80 disabled:opacity-50"
            aria-label="Delete story"
            data-testid="story-delete-btn"
          >
            <Trash2 className="w-4 h-4 text-white" />
          </button>
        )}
        <button
          onClick={onClose}
          className={`${isOwner ? "" : "ml-auto"} w-9 h-9 grid place-items-center rounded-full bg-black/40 backdrop-blur`}
          aria-label="Close"
          data-testid="story-close-btn"
        >
          <X className="w-4 h-4 text-white" />
        </button>
      </div>

      {/* Media */}
      <div className="absolute inset-0 grid place-items-center" onClick={(e) => {
        const x = e.clientX;
        if (x < window.innerWidth / 3) prev();
        else next();
      }}>
        {story.media_type === "video" ? (
          <video src={fileUrl(story.media_path)} className="max-w-full max-h-full object-contain" autoPlay muted playsInline />
        ) : (
          <SmartImage src={fileUrl(story.media_path)} alt="" mode="smart_auto_fit" className="absolute inset-0 w-full h-full" testid={`story-media-${story.story_id || 'unknown'}`} />
        )}
      </div>

      {/* Caption */}
      {story.caption && (
        <div className="absolute bottom-12 left-4 right-4 z-10 text-center">
          <p className="text-white text-base font-semibold drop-shadow">{story.caption}</p>
        </div>
      )}
    </div>
  );
}

export default function StoriesStrip() {
  const [groups, setGroups] = useState([]);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [startIdx, setStartIdx] = useState(0);
  const { user } = useAuth();
  const navigate = useNavigate();
  const fileInput = useRef(null);
  const [uploading, setUploading] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/stories");
      setGroups(data);
    } catch { /* ignore */ }
  };

  useEffect(() => { load(); }, []);

  const uploadStory = async (file) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await api.post("/stories", {
        media_path: data.path,
        media_type: file.type.startsWith("video") ? "video" : "image",
        caption: "",
      });
      toast.success("Story posted!");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't post story");
    } finally {
      setUploading(false);
    }
  };

  const onPickFile = (e) => {
    const f = e.target.files?.[0];
    if (f) uploadStory(f);
    e.target.value = "";
  };

  return (
    <div className="px-3 py-3 overflow-x-auto no-scrollbar" data-testid="stories-strip">
      <div className="flex items-center gap-3">
        {/* "Your story" — only for vendors */}
        {user?.is_vendor && (
          <>
            <button
              onClick={() => fileInput.current?.click()}
              className="flex flex-col items-center gap-1.5 shrink-0"
              data-testid="add-story-btn"
              disabled={uploading}
            >
              <span className="relative w-16 h-16 rounded-full bg-white/5 border-2 border-dashed border-pink-400 grid place-items-center">
                <Camera className="w-6 h-6 text-pink-300" />
                <span className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full bg-pink-500 grid place-items-center text-white border-2 border-black">
                  <Plus className="w-3 h-3" />
                </span>
              </span>
              <span className="text-[10px] text-zinc-300 font-bold">{uploading ? "Posting…" : "Your story"}</span>
            </button>
            <input ref={fileInput} type="file" accept="image/*,video/*" className="hidden" onChange={onPickFile} data-testid="story-file-input" />
          </>
        )}

        {groups.length === 0 && !user?.is_vendor && (
          <div className="text-xs text-zinc-500 px-2">No active stories yet.</div>
        )}

        {groups.map((g, idx) => (
          <button
            key={g.vendor_id}
            onClick={() => { setStartIdx(idx); setViewerOpen(true); }}
            className="flex flex-col items-center gap-1.5 shrink-0"
            data-testid={`story-vendor-${g.vendor_id}`}
          >
            <span className="w-16 h-16 rounded-full p-[2px] bg-gradient-to-tr from-pink-500 via-rose-400 to-cyan-400">
              <span className="block w-full h-full rounded-full overflow-hidden bg-black p-[2px]">
                <span className="block w-full h-full rounded-full overflow-hidden">
                  {g.vendor_avatar ? (
                    <img src={fileUrl(g.vendor_avatar)} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <span className="w-full h-full grid place-items-center bg-zinc-800 text-white font-black">
                      {g.vendor_name[0]}
                    </span>
                  )}
                </span>
              </span>
            </span>
            <span className="text-[10px] text-zinc-200 max-w-[64px] truncate font-bold">@{g.vendor_name}</span>
          </button>
        ))}
      </div>

      <AnimatePresence>
        {viewerOpen && (
          <StoryViewer
            groups={groups}
            startIndex={startIdx}
            currentUserId={user?.user_id}
            onClose={() => setViewerOpen(false)}
            onDeleted={(deletedId) => {
              setGroups((prev) => prev
                .map((g) => ({ ...g, stories: g.stories.filter((s) => s.story_id !== deletedId) }))
                .filter((g) => g.stories.length > 0));
            }}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
